/**
 * Analysis-register leak detection for the reflex's spoken stream — gpt-oss harmony sometimes writes its
 * deliberation into the `final` channel ("We have task completed. Need to retrieve result…", session
 * 20260923-101910 msg 33). Only the text can tell, and one regex cannot be both precise and complete (two
 * rewrites traded false positives for recall), so detection is two-stage:
 *
 *  1. `isLeakSuspect` — cheap, HIGH-RECALL text screen (may over-flag): the vocabulary deliberation can't avoid
 *     (the user/assistant as roles, instructions/guidelines, the reflex's own tool names, obligation modals,
 *     reply-planning verbs, protocol syntax). Saved-session sweep 2026-09-23 (934 reflex messages, 21 real leaks):
 *     28/913 normal replies (3.1%) hold a suspect sentence and pay for stage 2, 18 (2.0%) on the first sentence.
 *  2. `judgeLeak` — a typed-decision noul (Jev) on the suspect sentence + the user's last message: is this the
 *     assistant talking to itself, or words for the listener?
 *
 * `isStrictLeak` — a small HIGH-PRECISION subset, cut without a call (with or without a decider, and when the judge
 * fails). TRADE-OFF without a decider: suspects outside the strict set are SPOKEN — a leak phrased outside it
 * ("We need to answer the question.", "We should produce the answer now.", tests/analysis-leak.test.ts JUDGE_ONLY)
 * reaches TTS, but legit speech is never muted on a guess.
 */
import type { Decider } from './instinct';

/** The reflex's own tool names — protocol vocabulary. Case-sensitive: "Act"/"Think"/"Hold" are English words,
 *  the capitalized identifier is not ("Hold on" is speech). */
const TOOL = String.raw`(?:QuickLook|TaskStatus|CancelTask|AnswerTask|ExitSession|Act|Think|Hold(?! on\b))`;

const SUSPECT: RegExp[] = [
  /\b(?:user|assistant)\b/i,
  /\b(?:instructions?|guidelines?|polic(?:y|ies)|system prompt|developer (?:message|instructions?|prompt))\b/i,
  new RegExp(String.raw`\b${TOOL}\b`),
  /\bwe (?:need to|have to|should|must|ought to)\b|(?:^|[.!?]\s*)(?:need to|should|must|let's)\b/i,
  /\bi (?:need to|have to|should|must) (?:respond|reply|answer|output|produce|comply|acknowledge|say|ask|greet|call|use|dispatch|wait|keep|avoid|not)\b/i,
  /\b(?:respond|reply|output|produce|comply|acknowledge?)\w*\b/i,
  /\bwe(?: have|'ve)\b|\bwe (?:already )?(?:responded|replied|dispatched|used|called|did|gave|said|asked)\b/i,
  /\bno (?:further|more|other)\b/i,
  /\[\s*task\b|\bt\d+\b|\bwait(?:ing)? for (?:the )?(?:events?|results?|task)\b/i,
  /\w\s*\{\s*"|\bwhat:\s*"/,
  /\b(?:last|previous) (?:response|reply|message|turn)\b|\bthe (?:conversation|transcript|context)\b/i,
];

const STRICT: RegExp[] = [
  // the listener as a third-person subject opening the sentence ("The user wants…", telegraphic "User wants…")
  /^(?:the )?user(?:'s)? (?:asks|asked|wants|wanted|said|says|is (?:asking|saying|speaking|referring|requesting)|just (?:said|asked)|requests|requested|mentioned|seems|keeps|repeated|typed|provided|expects|explicitly)\b/i,
  /\b(?:if|when|since|because) user (?:is|keeps|wants|says|said|asks|asked|seems)\b/i,
  /\bwe have (?:an? )?(?:user (?:who|request|message|asking|saying)|conversation (?:where|with)|new user message)\b|^we have task\b/i,
  /\bthe assistant(?:'s (?:context|last|previous) (?:response|reply|turn|message)| (?:responded|replied) with)\b/i,
  // planning its own reply
  /\b(?:we|i) (?:need to|have to|should|must) (?:respond|reply|answer)(?:\s*:|\s+(?:briefly|concisely|accordingly|according to|appropriately|to the user|with (?:a |the )?(?:brief|short|result|acknowledg)))/i,
  /\b(?:we|i) (?:need to|should|must) output\b|^(?:we|i) must comply[.!]?$/i,
  /\bno (?:further|more|other) (?:response|reply|output)s? (?:is )?(?:needed|required|necessary)\b/i,
  /\bwe(?: have|'ve)? (?:already )?(?:responded|replied)[.!]?$/i,
  // its own protocol: using its tools, tool-call syntax, task events
  new RegExp(String.raw`(?:\b[Ww]e(?: need to| should| must| have to| will|'ll)|\b[Ll]et'?s|^[Ss]hould|^[Nn]ow we should) (?:call|use|do|dispatch(?: to)?|invoke) (?:the )?${TOOL}\b`),
  new RegExp(String.raw`\b(?:[Ww]e (?:called|dispatched)|responded with) ${TOOL}\b|\b${TOOL}\s*\{|\bwhat:\s*"|\b${TOOL} (?:tool )?(?:brief|call|args?|arguments)\b`),
  /\bwait(?:ing)? for (?:the )?(?:events?|t\d+)\b|\bt\d+ (?:completed|failed)\b|\[\s*task\b/i,
];

/** Sentence boundary: a terminator (+ closing quote/bracket) then whitespace, or glued straight onto a capital or an
 *  [emotion] tag — step outputs arrive glued ("…files now.Okay, still working.We should output…", corpus
 *  20260623-101805 #40; "…?We have responded.[neutral] …", 20260630-184310 #28). */
export const SENTENCE_END = /[.!?…]+["')\]]*(?:\s|(?=\p{Lu}|\[))/gu;
export const SENTENCE_SPLIT = /(?<=[.!?…]+["')\]]*)(?:\s+|(?=\p{Lu}|\[))/u;

const clean = (s: string) => s.replace(/\[[a-z_ ]+\]\s*/gi, '').trim(); // emotion tags are not words

/** Stage 1: could this sentence be deliberation? Over-flags by design — see module doc. */
export function isLeakSuspect(sentence: string): boolean {
  const s = clean(sentence);
  return SUSPECT.some((re) => re.test(s));
}

/** High-precision leak shapes — muted without a judge (and on judge failure). */
export function isStrictLeak(sentence: string): boolean {
  return sentence.split(SENTENCE_SPLIT).some((x) => { const s = clean(x); return STRICT.some((re) => re.test(s)); });
}

const JUDGE_INSTRUCTIONS =
  'This sentence was produced by a voice assistant\'s reply stream and is about to be read aloud to the user. ' +
  'Is it the assistant\'s private scratchpad reasoning that leaked out — talking ABOUT the conversation instead of TO the listener? ' +
  'Leaked reasoning: refers to the listener in the third person ("the user wants…", "user said…"), plans its own reply ("we need to respond…", ' +
  '"we should output…", "I should answer briefly"), cites its own guidelines/instructions, or narrates its internal tool calls and task events ' +
  '(QuickLook, Act, Think, Hold, TaskStatus, "t2 completed") as steps it is taking. ' +
  'Speech: anything said TO the listener — answers, questions, offers, news, advice, explaining what it can do when asked, even if it mentions ' +
  'a user, an assistant, a policy, instructions, replying, or a tool.';

/** Judge verdicts at/above this are muted. Live eval (tests/analysis-leak.test.ts lists, 2026-09-23): 0.6 kept every
 *  listed false positive but one speaking; 0.5 muted four more of them for one extra caught leak. */
export const LEAK_JUDGE_THRESHOLD = 0.6;

/** Stage 2: the typed-decision judge — probability (0..1) that `sentence` is deliberation, not speech. Throws on
 *  decider failure (the caller picks its fallback). */
export async function judgeLeak(decider: Decider, sentence: string, userSaid: string): Promise<number> {
  const { answers } = await decider.decide({
    state: { what_the_user_just_said: userSaid.slice(-600) || null, sentence_about_to_be_spoken: clean(sentence) },
    questions: {
      q: {
        type: 'noul', instructions: JUDGE_INSTRUCTIONS,
        criteria: { true: 'leaked private reasoning — must not be spoken', false: 'said to the listener — fine to speak' },
      },
    },
  });
  const p = Number((answers.q as unknown as { noul?: number }).noul);
  if (!Number.isFinite(p)) throw new Error('leak judge: no noul in the answer');
  return p;
}
